import { supabase } from '@/lib/supabase';
import { apiPost } from '@/lib/apiFetch';

export interface OrderAssignedData {
  subject: string;
  order_number: string;
  build_sku: string;
  technician_name: string;
  technician_email: string;
  quantity: number;
  build_type: string;
  to_number: string;
  assigned_by: string;
  date: string;
  notes: string;
  build_comments: string;
}

export interface OrderSubmittedForReviewData {
  subject: string;
  order_number: string;
  build_sku: string;
  technician_name: string;
  serial_number: string;
  to_number: string;
  is_managed: boolean;
  submitted_at: string;
}

/**
 * Hand the payload to api/notify, which holds the flow URLs and does the POST.
 * Resolves false rather than throwing: every caller treats notification as
 * fire-and-forget, and a failed notification must not fail the work that
 * triggered it. The reason is logged server-side either way.
 */
async function postWebhook(kind: string, data: Record<string, unknown>): Promise<boolean> {
  try {
    await apiPost('/api/notify', { kind, payload: data });
    return true;
  } catch (err) {
    console.error(`Notification "${kind}" failed:`, err);
    return false;
  }
}

/** Check if a technician is managed (has a managed_by value) */
async function isTechManaged(technicianId: string): Promise<boolean> {
  const { data } = await supabase
    .from('technicians')
    .select('managed_by')
    .eq('id', technicianId)
    .single();
  return !!data?.managed_by;
}

/** Look up a technician's email straight off their technicians row */
async function getTechEmail(technicianId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('technicians')
    .select('email')
    .eq('id', technicianId)
    .single();

  if (error) {
    console.error('getTechEmail failed:', error);
    return null;
  }
  return data?.email ?? null;
}

/** Notify technician that an order has been assigned to them */
export async function notifyOrderAssigned(
  data: Omit<OrderAssignedData, 'technician_email'>,
  technicianId: string,
): Promise<boolean> {
  const email = await getTechEmail(technicianId);
  if (!email) {
    console.error('No email for technician', data.technician_name, '- notification will fall back');
  }
  return postWebhook('order-assigned', {
    ...data,
    // The flow's trigger schema types every property as a string. Sending a
    // raw number fails validation with TriggerInputSchemaMismatch, which is
    // rejected before a run is created - so it shows up as the flow never
    // firing rather than as a failed run.
    quantity: String(data.quantity),
    technician_email: email || '',
  } as unknown as Record<string, unknown>);
}

/** Notify admins/managers that an order has been submitted for review */
export async function notifyOrderSubmittedForReview(
  data: Omit<OrderSubmittedForReviewData, 'is_managed' | 'subject'>,
  technicianId: string,
): Promise<boolean> {
  const managed = await isTechManaged(technicianId);
  const subject = `Automated Message - ${data.order_number} Complete - ${data.to_number} - ${data.technician_name}`;
  return postWebhook('order-review', {
    ...data,
    subject,
    is_managed: managed,
  } as unknown as Record<string, unknown>);
}
